import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * The last class wins, which is what lets a caller pass `className` to override
 * a component's own utilities instead of racing them in source order.
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * React Aria's `className` accepts a function of render props. Components here
 * take a plain string, so this adapts one to the other without every component
 * spelling out the generic.
 */
export function classNameFor<T>(
  base: string,
  extra?: string | ((values: T) => string),
): (values: T) => string {
  return (values) => cn(base, typeof extra === 'function' ? extra(values) : extra);
}

/**
 * Shared focus treatment. React Aria sets `data-focus-visible` only for
 * keyboard focus, so a mouse click does not paint a ring — which is why the
 * app's base stylesheet turns the browser's own `:focus-visible` outline off
 * and defers to this.
 *
 * `focus-ring` is a custom utility in `styles.css` rather than the three
 * `outline-*` utilities it replaces: the ring colour has to flip between light
 * and dark mode, and the `outline-*` scale does not.
 */
export const FOCUS_RING = 'data-focus-visible:focus-ring';
